import React, { useState, useEffect } from "react";
import { useCredits } from "./hooks/useCredits";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from "components/ui/table";
import { Search, Wallet, Trash2, AlertCircle } from "lucide-react";

const formatCurrency = (value) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(
    value || 0
  );

function CreditsTab() {
  const { credits, isLoading, fetchCredits, deleteCredit } = useCredits();
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    fetchCredits();
  }, [fetchCredits]);

  const filteredCredits = credits.filter(
    (c) =>
      c.clientName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.phone.includes(searchTerm)
  );

  const totalCreditsValue = filteredCredits.reduce(
    (acc, curr) => acc + curr.value,
    0
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Wallet className="h-6 w-6 text-green-600" /> Gestão de Créditos
          </h2>
          <p className="text-muted-foreground text-sm">
            Gerencie saldos gerados por cancelamentos.
          </p>
        </div>

        {/* Card de Resumo Rápido */}
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-100 dark:border-green-800 p-3 rounded-lg flex flex-col items-end">
          <span className="text-xs text-muted-foreground uppercase font-bold">
            Total em Aberto
          </span>
          <span className="text-2xl font-bold text-green-700 dark:text-green-400">
            {formatCurrency(totalCreditsValue)}
          </span>
        </div>
      </div>

      <div className="flex items-center space-x-2">
        <Search className="h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Buscar por nome ou telefone..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="max-w-sm"
        />
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Cliente</TableHead>
              <TableHead>Telefone</TableHead>
              <TableHead>Valor</TableHead>
              <TableHead>Validade</TableHead>
              <TableHead className="text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-8">
                  Carregando créditos...
                </TableCell>
              </TableRow>
            ) : filteredCredits.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={5}
                  className="text-center py-8 text-muted-foreground"
                >
                  Nenhum crédito encontrado.
                </TableCell>
              </TableRow>
            ) : (
              filteredCredits.map((credit) => {
                const isExpired =
                  credit.expirationDate && new Date() > credit.expirationDate;

                return (
                  <TableRow
                    key={credit.id}
                    className={isExpired ? "bg-red-50 dark:bg-red-900/10" : ""}
                  >
                    <TableCell className="font-medium">
                      {credit.clientName}
                    </TableCell>
                    <TableCell>{credit.phone}</TableCell>
                    <TableCell className="font-bold text-green-600 dark:text-green-400">
                      {formatCurrency(credit.value)}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {credit.expirationDate
                          ? credit.expirationDate.toLocaleDateString("pt-BR")
                          : "-"}
                        {isExpired && (
                          <span className="text-xs font-bold text-red-500 flex items-center gap-1 border border-red-200 bg-red-100 px-1.5 rounded">
                            <AlertCircle className="h-3 w-3" /> Vencido
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-destructive hover:text-destructive hover:bg-destructive/10"
                        onClick={() => deleteCredit(credit.id)}
                        title="Dar baixa / Utilizar crédito"
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Baixar
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

export default CreditsTab;
